import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Linkedin, Twitter, Facebook, Mail, Globe } from 'lucide-react';
import { useTranslation, Language } from '../hooks/useTranslation';

const Footer = () => {
  const { t, lang, setLanguage, isBrazil } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const currentYear = new Date().getFullYear();

  const sections = [
    {
      title: t.features,
      links: [
        { name: t.features, href: '/features' },
        { name: t.pricing, href: '/pricing' },
        { name: t.howItWorks, href: '/how-it-works' },
        { name: 'FAQ', href: '/faq' },
      ],
    },
    {
      title: t.platform,
      links: [
        { name: 'Instagram', href: '/platforms/instagram' },
        { name: 'LinkedIn', href: '/platforms/linkedin' },
        { name: 'Facebook', href: '/platforms/facebook' },
        { name: 'TikTok', href: '/platforms/tiktok' },
        { name: 'Twitter / X', href: '/platforms/x-twitter' },
      ],
    },
    {
      title: t.useCases,
      links: [
        { name: t.smallBusinesses, href: '/use-cases/small-businesses' },
        { name: t.agencies, href: '/use-cases/agencies' },
        { name: t.creators, href: '/use-cases/creators' },
        { name: t.marketingTeams, href: '/use-cases/marketing-teams' },
      ],
    },
    {
      title: t.company,
      links: [
        { name: t.about, href: '/about' },
        { name: t.portfolio, href: '/showcase' },
        { name: t.blog, href: '/blog' },
        { name: t.contact, href: '/contact' },
        { name: t.privacyPolicy, href: '/privacy-policy' },
        { name: t.termsOfService, href: '/terms' },
      ],
    },
  ];

  return (
    <footer className="bg-[#000112] border-t border-white/5 pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-12 mb-20">
          {/* Brand Info */}
          <div className="col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-8">
              <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                <img src="/PlataPost-P.png" alt="P" className="w-10 h-10 object-contain" />
              </div>
              <span className="text-2xl font-black tracking-tighter text-white">PlataPost</span>
            </Link>
            <p className="text-sm mb-8 max-w-xs leading-relaxed text-gray-400">
              {t.heroSubtitle}
            </p>
            <div className="flex items-center gap-4 mb-8">
              {[Instagram, Linkedin, Twitter, Facebook].map((Icon, i) => (
                <a key={i} href="#" className="p-2 rounded-xl transition-all shadow-sm text-gray-400 hover:text-white hover:bg-white/5">
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Language Switcher */}
            <div className="pt-6 border-t border-white/5">
              <div className="flex items-center gap-2 mb-4 text-gray-500">
                <Globe className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">{t.language}</span>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${lang === 'en' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                >
                  English
                </button>
                <button 
                  onClick={() => setLanguage('br')}
                  className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${lang === 'br' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                >
                  Português
                </button>
              </div>
            </div>
          </div>

          {/* Nav Sections */}
          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="font-black uppercase tracking-widest text-[10px] mb-6 text-indigo-400">{section.title}</h4>
              <ul className="space-y-4">
                {section.links.map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className="text-sm font-medium transition-colors text-gray-400 hover:text-white">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-12 border-t flex flex-col md:flex-row justify-between items-center gap-6 text-sm border-white/5 text-gray-500">
          <p>© {currentYear} PlataPost AI. {t.rightsReserved}</p>
          <div className="flex items-center gap-8">
            <Link to="/sitemap.xml" className="hover:text-indigo-600 transition-colors">Sitemap</Link>
            <a href="mailto:support@platapost.com" className="flex items-center gap-2 hover:text-indigo-600 transition-colors">
              <Mail className="w-4 h-4" />
              support@platapost.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
